import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle2, Upload, FileText, X } from 'lucide-react';
import { Seo } from '@/components/common/Seo';
import { PageHero } from '@/components/common/PageHero';
import { OphirButton } from '@/components/common/OphirButton';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { submitRfq, uploadFile } from '@/lib/api';
import { DISCLAIMER, B2B_CATEGORIES } from '@/data/b2b';

const L = 'text-[12px] font-semibold uppercase tracking-[0.1em] text-ophir-navy';
const FREQ = ['One-Time Purchase', 'Weekly', 'Monthly', 'Quarterly', 'Ongoing / Replenishment'];

export default function RequestBulkQuote() {
  const [searchParams] = useSearchParams();
  const presetCategory = searchParams.get('category') || '';
  const presetSource = searchParams.get('source') || 'request-bulk-quote';
  const [f, setF] = useState({
    full_name: '', company_name: '', business_email: '', phone: '', website: '',
    business_type: '', industry: '', country: '', province_state: '', shipping_destination: '',
    product_name: '', brand: '', upc_sku: '',
    product_category: B2B_CATEGORIES.includes(presetCategory) ? presetCategory : '',
    quantity: '', case_pallet: '',
    target_price: '', frequency: '', details: '', document_ids: [], documents_meta: [],
    source_page: presetSource, consent: false, company_website_hp: '',
  });
  const [errors, setErrors] = useState({});
  const [uploading, setUploading] = useState(false);
  const [pct, setPct] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const onFiles = async (list) => {
    for (const file of Array.from(list || [])) {
      if (file.size > 15 * 1024 * 1024) { toast.error(`${file.name} exceeds 15 MB.`); continue; }
      setUploading(true); setPct(0);
      try {
        const m = await uploadFile(file, setPct);
        setF((p) => ({ ...p, document_ids: [...p.document_ids, m.id], documents_meta: [...p.documents_meta, m] }));
        toast.success(`Uploaded ${m.original_name}`);
      } catch (e) { toast.error(`Failed to upload ${file.name}`); }
      finally { setUploading(false); setPct(0); }
    }
  };
  const removeDoc = (id) => setF((p) => ({ ...p, document_ids: p.document_ids.filter((x) => x !== id), documents_meta: p.documents_meta.filter((m) => m.id !== id) }));

  const validate = () => {
    const e = {};
    if (!f.full_name.trim()) e.full_name = 'Required';
    if (!f.company_name.trim()) e.company_name = 'Required';
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.business_email)) e.business_email = 'Enter a valid email';
    if (!f.country.trim()) e.country = 'Required';
    if (!f.consent) e.consent = 'Consent is required';
    setErrors(e); return Object.keys(e).length === 0;
  };
  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) { toast.error('Please complete the required fields.'); return; }
    setSubmitting(true);
    try { const { documents_meta, ...payload } = f; await submitRfq(payload); setDone(true); window.scrollTo(0, 0); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Something went wrong.'); }
    finally { setSubmitting(false); }
  };

  if (done) return (
    <>
      <Seo title="Bulk Quote Request Received | Ophir Supply" description="Your procurement request has been received." />
      <PageHero label="Request Bulk Quote" title="Request received" />
      <section className="bg-white"><div className="container-ophir py-16">
        <div data-testid="rfq-success" className="max-w-2xl bg-ophir-ivory-soft border border-ophir-grey rounded-2xl p-8 shadow-ophir">
          <CheckCircle2 className="text-ophir-navy" size={44} />
          <h2 className="mt-4 font-serif text-[26px] text-ophir-navy-deep">Thank you</h2>
          <p className="mt-4 text-[15.5px] leading-relaxed text-ophir-muted">Your procurement request has been received. Our team will review the information provided and contact you regarding available sourcing options.</p>
          <div className="mt-6"><OphirButton to="/">Return Home</OphirButton></div>
        </div>
      </div></section>
    </>
  );

  const Err = ({ k }) => errors[k] ? <p className="mt-1 text-[13px] text-[#B42318]">{errors[k]}</p> : null;

  return (
    <>
      <Seo title="Request a Bulk Supply Quote (RFQ) | Ophir Supply" description="Tell us what your organization needs and our procurement team will review available sourcing options across our supplier network." />
      <PageHero label="Request Bulk Quote" title="Request a bulk supply quote" intro="Tell us what your organization needs and our procurement team can review your request and determine available sourcing options." />
      <section className="bg-white">
        <div className="container-ophir py-14 lg:py-20">
          <form data-testid="rfq-form" onSubmit={onSubmit} noValidate className="max-w-3xl space-y-8">
            <input type="text" tabIndex={-1} autoComplete="off" className="hidden" value={f.company_website_hp} onChange={(e) => set('company_website_hp', e.target.value)} aria-hidden="true" />

            <fieldset className="space-y-5">
              <legend className="font-serif text-[20px] text-ophir-navy-deep mb-2">Contact information</legend>
              <div className="grid sm:grid-cols-2 gap-5">
                <div><Label className={L}>Full name *</Label><Input data-testid="rfq-full-name" value={f.full_name} onChange={(e) => set('full_name', e.target.value)} className="mt-2" /><Err k="full_name" /></div>
                <div><Label className={L}>Company name *</Label><Input data-testid="rfq-company" value={f.company_name} onChange={(e) => set('company_name', e.target.value)} className="mt-2" /><Err k="company_name" /></div>
                <div><Label className={L}>Business email *</Label><Input type="email" data-testid="rfq-email" value={f.business_email} onChange={(e) => set('business_email', e.target.value)} className="mt-2" /><Err k="business_email" /></div>
                <div><Label className={L}>Phone number</Label><Input data-testid="rfq-phone" value={f.phone} onChange={(e) => set('phone', e.target.value)} className="mt-2" /></div>
                <div className="sm:col-span-2"><Label className={L}>Company website</Label><Input data-testid="rfq-website" value={f.website} onChange={(e) => set('website', e.target.value)} className="mt-2" placeholder="https://" /></div>
              </div>
            </fieldset>

            <fieldset className="space-y-5">
              <legend className="font-serif text-[20px] text-ophir-navy-deep mb-2">Business information</legend>
              <div className="grid sm:grid-cols-2 gap-5">
                <div><Label className={L}>Business type</Label><Input data-testid="rfq-business-type" value={f.business_type} onChange={(e) => set('business_type', e.target.value)} className="mt-2" /></div>
                <div><Label className={L}>Industry</Label><Input data-testid="rfq-industry" value={f.industry} onChange={(e) => set('industry', e.target.value)} className="mt-2" /></div>
                <div><Label className={L}>Country *</Label><Input data-testid="rfq-country" value={f.country} onChange={(e) => set('country', e.target.value)} className="mt-2" /><Err k="country" /></div>
                <div><Label className={L}>State / province</Label><Input data-testid="rfq-province" value={f.province_state} onChange={(e) => set('province_state', e.target.value)} className="mt-2" /></div>
                <div className="sm:col-span-2"><Label className={L}>Shipping destination</Label><Input data-testid="rfq-destination" value={f.shipping_destination} onChange={(e) => set('shipping_destination', e.target.value)} className="mt-2" /></div>
              </div>
            </fieldset>

            <fieldset className="space-y-5">
              <legend className="font-serif text-[20px] text-ophir-navy-deep mb-2">Product information</legend>
              <div className="grid sm:grid-cols-2 gap-5">
                <div><Label className={L}>Product name</Label><Input data-testid="rfq-product" value={f.product_name} onChange={(e) => set('product_name', e.target.value)} className="mt-2" /></div>
                <div><Label className={L}>Brand</Label><Input data-testid="rfq-brand" value={f.brand} onChange={(e) => set('brand', e.target.value)} className="mt-2" /></div>
                <div><Label className={L}>UPC / SKU</Label><Input data-testid="rfq-sku" value={f.upc_sku} onChange={(e) => set('upc_sku', e.target.value)} className="mt-2" /></div>
                <div><Label className={L}>Product category</Label>
                  <Select value={f.product_category} onValueChange={(v) => set('product_category', v)}>
                    <SelectTrigger data-testid="rfq-category" className="mt-2"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{B2B_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className={L}>Desired quantity</Label><Input data-testid="rfq-quantity" value={f.quantity} onChange={(e) => set('quantity', e.target.value)} className="mt-2" /></div>
                <div><Label className={L}>Case / pallet requirement</Label><Input data-testid="rfq-case" value={f.case_pallet} onChange={(e) => set('case_pallet', e.target.value)} className="mt-2" /></div>
                <div><Label className={L}>Target price</Label><Input data-testid="rfq-price" value={f.target_price} onChange={(e) => set('target_price', e.target.value)} className="mt-2" /></div>
                <div><Label className={L}>Purchase frequency</Label>
                  <Select value={f.frequency} onValueChange={(v) => set('frequency', v)}>
                    <SelectTrigger data-testid="rfq-frequency" className="mt-2"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{FREQ.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </fieldset>

            <fieldset className="space-y-4">
              <legend className="font-serif text-[20px] text-ophir-navy-deep mb-2">Additional information</legend>
              <Textarea data-testid="rfq-details" rows={5} value={f.details} onChange={(e) => set('details', e.target.value)} placeholder="Tell us about the products, quantities, specifications, brands or procurement requirements you need." />
              <label className="flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-ophir-grey bg-ophir-ivory-soft px-6 py-8 cursor-pointer hover:border-ophir-gold transition-colors">
                <Upload className="text-ophir-navy" size={24} />
                <span className="text-[14.5px] font-semibold text-ophir-navy">Attach product lists, RFQs, SKU lists or spreadsheets (optional)</span>
                <span className="text-[12.5px] text-ophir-muted">PDF, Word, Excel, CSV or images · max 15 MB</span>
                <input data-testid="rfq-file-upload" type="file" multiple className="hidden" onChange={(e) => onFiles(e.target.files)} accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,image/*" />
              </label>
              {uploading && <div><Progress value={pct} className="h-2" /><p className="mt-1 text-[13px] text-ophir-muted">Uploading… {pct}%</p></div>}
              {f.documents_meta.length > 0 && (
                <ul className="space-y-2">{f.documents_meta.map((m) => (
                  <li key={m.id} className="flex items-center justify-between rounded-lg border border-ophir-grey bg-white px-4 py-3">
                    <span className="flex items-center gap-3 text-[14px] text-ophir-navy min-w-0"><FileText size={18} className="text-ophir-gold-deep shrink-0" /><span className="truncate">{m.original_name}</span></span>
                    <button type="button" onClick={() => removeDoc(m.id)} className="text-ophir-muted hover:text-[#B42318]"><X size={18} /></button>
                  </li>))}
                </ul>
              )}
            </fieldset>

            <div className="rounded-xl border border-ophir-grey bg-ophir-ivory-soft p-5 text-[13px] leading-relaxed text-ophir-muted">{DISCLAIMER}</div>
            <div className="flex items-start gap-3">
              <Checkbox id="rfq-consent" data-testid="rfq-consent" checked={f.consent} onCheckedChange={(v) => set('consent', !!v)} className="mt-1" />
              <Label htmlFor="rfq-consent" className="text-[14px] font-normal leading-relaxed text-ophir-muted">I consent to Ophir Supply storing and using this information to review my procurement request. *</Label>
            </div>
            {errors.consent && <p className="text-[13px] text-[#B42318]">{errors.consent}</p>}
            <OphirButton type="submit" data-testid="rfq-submit" disabled={submitting}>{submitting ? 'Submitting…' : 'Submit Bulk Quote Request'}</OphirButton>
          </form>
        </div>
      </section>
    </>
  );
}
